/**
 * The 404 for unmatched client routes.
 *
 * The root one sits outside the shell, so it brings its own page frame: an
 * unmatched address may not belong to a signed-in workspace at all, and the
 * tenant-refusal path lands on it. An address inside a known area, such as a
 * programme, gets `NotFoundPage` under that area's own chrome instead.
 *
 * @module router/NotFoundRoute
 */

import { composePageTitle } from "@/components/shell/page-title";
import { useDocumentTitle } from "@/components/shell/RouteTitle";
import { EmptyState, LinkButton, PageContainer } from "@/components/ui";

/** The message, for a caller that already supplies the page's frame and landmark. */
export function NotFoundPage() {
  return (
    <EmptyState
      icon="compass"
      headingLevel={1}
      title="Page not found"
      description="There's nothing at this address."
      action={
        <LinkButton to="/" variant="primary" icon="house">
          Back to overview
        </LinkButton>
      }
    />
  );
}

export function NotFoundRoute() {
  // Nothing above this route names the tab, so the last title set (a sign-in
  // screen's, say) would otherwise stay.
  useDocumentTitle(composePageTitle(["Page not found"]));
  return (
    // Its own main: this route is a sibling of the shell, so nothing above it
    // opens one, and without it the page has no landmark to skip to at all.
    <main id="main-content">
      <PageContainer>
        <NotFoundPage />
      </PageContainer>
    </main>
  );
}
